import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Download, RefreshCw, ExternalLink, FileArchive, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AdminMedianSubmission
 *
 * One-screen control surface for the Median.co handoff bundle.
 *
 * What the operator can do here:
 *   • See whether the bundle is built, when it was built, and how big it is
 *   • Rebuild it on-demand (picks up any logo / screenshot / doc updates)
 *   • Download the latest zip with one click — streamed through the
 *     admin-protected endpoint so it never leaks publicly
 *
 * Why not auto-rebuild on every download?
 *   Bundle generation takes seconds AND screenshots, but is destructive
 *   (overwrites the existing zip). Making it an explicit operator action
 *   prevents the operator from accidentally shipping a half-baked
 *   pre-redeploy bundle to Median.
 */
export default function AdminMedianSubmission() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const fetchInfo = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/median-bundle/info");
      setInfo(data);
    } catch (e) {
      toast.error("Couldn't load bundle metadata");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post("/admin/median-bundle/rebuild", {});
      toast.success(`Rebuilt — ${data.filesPacked} files, ${data.sizeMB} MB`);
      await fetchInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const download = async () => {
    try {
      const resp = await api.get("/admin/median-bundle.zip", {
        responseType: "blob",
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cauldron-median-handoff.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const modifiedDate = info?.modifiedAt
    ? new Date(info.modifiedAt * 1000).toLocaleString()
    : null;

  return (
    <div className="space-y-6" data-testid="admin-median-submission">
      <div>
        <h2 className="font-serif text-xl">Submit to Median.co</h2>
        <p className="text-sm text-foreground/60 mt-1">
          Download the prepared handoff bundle and upload it to Median.co&apos;s
          intake form. They&apos;ll submit Cauldron to the Apple App Store and
          Google Play in ~5–7 days.
        </p>
      </div>

      {/* Bundle status card */}
      <div className="parchment border border-secondary/15 rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 rounded bg-secondary/15 flex items-center justify-center">
            <FileArchive size={22} className="text-secondary" strokeWidth={1.5} />
          </div>

          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="text-sm text-foreground/60 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Checking bundle…
              </div>
            ) : !info?.exists ? (
              <div className="text-sm text-amber-400">
                Bundle hasn&apos;t been built yet. Click <b>Rebuild</b> to generate it.
              </div>
            ) : (
              <div className="space-y-1">
                <div className="font-mono text-sm">cauldron-median-handoff.zip</div>
                <div className="text-xs text-foreground/55">
                  {info.sizeMB} MB · last built {modifiedDate}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-5">
              <button
                onClick={download}
                disabled={!info?.exists || loading}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-xs uppercase tracking-[0.18em] hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="median-bundle-download"
              >
                <Download size={14} /> Download zip
              </button>
              <button
                onClick={rebuild}
                disabled={rebuilding}
                className="inline-flex items-center gap-2 border border-secondary/40 text-secondary px-5 py-2.5 rounded-sm text-xs uppercase tracking-[0.18em] hover:bg-secondary/10 disabled:opacity-40"
                data-testid="median-bundle-rebuild"
              >
                {rebuilding ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {rebuilding ? "Rebuilding…" : "Rebuild bundle"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Submission checklist */}
      <div className="border border-secondary/15 rounded-sm overflow-hidden">
        <div className="bg-secondary/5 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-foreground/60">
          Submission flow
        </div>
        <ol className="divide-y divide-secondary/10">
          <Step n={1} title="Rebuild after any branding / copy change">
            Hit <b>Rebuild</b> whenever you swap the logo, regenerate screenshots, or edit the handoff docs in <code>/app/memory/</code>. Otherwise Median ships stale assets.
          </Step>
          <Step n={2} title="Sign up at median.co">
            Create an account at{" "}
            <a href="https://median.co" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline inline-flex items-center gap-1">
              median.co <ExternalLink size={11} />
            </a>{" "}
            and choose the &ldquo;Premium&rdquo; or &ldquo;Enterprise&rdquo; tier that includes App Store submission.
          </Step>
          <Step n={3} title="Upload this zip to their intake form">
            Their intake asks for branding, IAP product IDs, push notification setup, and screenshots — all included in <code>01-START-HERE-handoff.md</code> inside the zip.
          </Step>
          <Step n={4} title="Fill in Section 2 of the handoff doc">
            Median needs your Apple Developer Team ID, App-Specific Password, and Google Play console email. Fill these into Section 2 of <code>01-START-HERE-handoff.md</code> before sending.
          </Step>
          <Step n={5} title="Grant temporary App Manager / Release Manager access">
            Per the handoff doc, give Median&apos;s submitter agent App Store Connect &ldquo;App Manager&rdquo; access and Play Console &ldquo;Release Manager&rdquo; access. Revoke after launch.
          </Step>
          <Step n={6} title="~5–7 days later: live in both stores">
            Median submits, handles reviewer back-and-forth, and notifies you when both apps go live.
          </Step>
        </ol>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-4 px-5 py-4">
      <div className="shrink-0 w-7 h-7 rounded-full bg-secondary/15 text-secondary inline-flex items-center justify-center font-serif text-sm">
        {n}
      </div>
      <div>
        <div className="text-sm font-medium leading-tight">{title}</div>
        <div className="text-xs text-foreground/65 mt-1 leading-relaxed">{children}</div>
      </div>
    </li>
  );
}
